import { useUserStore, useWorkspaceStore } from '@/stores';
import { DeltaPacket } from '@/stores/realtime';
import { ResourceActivityResourceType, ResourceComment } from '@/types';

export const RESOURCE_COMMENT_EVENT = 'countercyclical:resource-comment';
export const RESOURCE_COMMENT_TARGET_FOCUS_EVENT = 'countercyclical:resource-comment-target-focus';
export const RESOURCE_ACTIVITY_OPEN_EVENT = 'countercyclical:resource-activity-open';

export type ResourceCommentEventAction = 'created' | 'deleted' | 'updated';

export type OptimisticResourceCommentInput = {
    component?: string;
    message: string;
    metadata?: Record<string, unknown>;
    parentId?: string;
};

export interface ResourceCommentEventDetail {
    action: ResourceCommentEventAction;
    comment?: ResourceComment;
    commentId?: string;
    resourceId: string;
    resourceType: ResourceActivityResourceType;
}

export type ResourceCommentDeltaPacket = DeltaPacket<'resourceComment', ResourceComment>;

export interface ResourceCommentTargetFocusEventDetail {
    commentId?: string;
    component?: string;
    resourceId: string;
    resourceType: ResourceActivityResourceType;
    target?: Record<string, unknown>;
}

export interface ResourceActivityOpenEventDetail {
    commentId?: string;
    resourceId: string;
    resourceType: ResourceActivityResourceType;
    tab?: 'activity' | 'comments';
}

const optimisticResourceCommentsByKey = new Map<string, ResourceComment[]>();
let pendingResourceCommentTargetFocus: ResourceCommentTargetFocusEventDetail | undefined;
let pendingResourceCommentTargetFocusTimer: ReturnType<typeof setTimeout> | undefined;

const optimisticResourceCommentsKey = (resourceType: ResourceActivityResourceType, resourceId: string) =>
    `${resourceType}:${resourceId}`;

const addOptimisticResourceComment = (
    resourceType: ResourceActivityResourceType,
    resourceId: string,
    comment: ResourceComment,
) => {
    const key = optimisticResourceCommentsKey(resourceType, resourceId);
    const comments = optimisticResourceCommentsByKey.get(key) ?? [];

    optimisticResourceCommentsByKey.set(key, [comment, ...comments.filter((item) => item.id !== comment.id)]);
};

const removeBufferedOptimisticResourceComment = (
    resourceType: ResourceActivityResourceType,
    resourceId: string,
    commentId: string,
) => {
    const key = optimisticResourceCommentsKey(resourceType, resourceId);
    const comments = optimisticResourceCommentsByKey.get(key)?.filter((comment) => comment.id !== commentId) ?? [];

    if (comments.length) {
        optimisticResourceCommentsByKey.set(key, comments);
        return;
    }

    optimisticResourceCommentsByKey.delete(key);
};

export const getOptimisticResourceComments = (resourceType: ResourceActivityResourceType, resourceId: string) =>
    optimisticResourceCommentsByKey.get(optimisticResourceCommentsKey(resourceType, resourceId)) ?? [];

export const createOptimisticResourceComment = (
    resourceType: ResourceActivityResourceType,
    resourceId: string,
    input: OptimisticResourceCommentInput,
): ResourceComment => {
    const user = useUserStore.getState().selectedUser;
    const workspace = useWorkspaceStore.getState().selectedWorkspace ?? user?.activeWorkspace;
    const now = new Date();
    const randomId = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    return {
        id: `optimistic:${randomId}`,
        workspace: workspace as ResourceComment['workspace'],
        user: user ?? undefined,
        member: user?.activeMember,
        resourceType,
        resourceId,
        component: input.component,
        parentId: input.parentId,
        message: input.message,
        resolved: false,
        metadata: input.metadata,
        createdAt: now,
        updatedAt: now,
    };
};

export const emitResourceCommentEvent = (detail: ResourceCommentEventDetail) => {
    window.dispatchEvent(new CustomEvent<ResourceCommentEventDetail>(RESOURCE_COMMENT_EVENT, { detail }));
};

export const emitResourceCommentDeltaPacket = (packet: ResourceCommentDeltaPacket) => {
    const comment = packet.payload;
    const resourceId = comment?.resourceId;
    const resourceType = comment?.resourceType;

    if (!resourceId || !resourceType) {
        return;
    }

    if (packet.operation === 'delete') {
        emitResourceCommentEvent({
            action: 'deleted',
            comment,
            commentId: packet.entityId,
            resourceId,
            resourceType,
        });

        return;
    }

    emitResourceCommentEvent({
        action: packet.operation === 'update' ? 'updated' : 'created',
        comment,
        resourceId,
        resourceType,
    });
};

export const emitOptimisticResourceCommentCreated = (
    resourceType: ResourceActivityResourceType,
    resourceId: string,
    input: OptimisticResourceCommentInput,
) => {
    const comment = createOptimisticResourceComment(resourceType, resourceId, input);

    addOptimisticResourceComment(resourceType, resourceId, comment);
    emitResourceCommentEvent({ action: 'created', comment, resourceId, resourceType });

    return comment;
};

export const replaceOptimisticResourceComment = (
    optimisticComment: ResourceComment,
    persistedComment: ResourceComment,
    resourceId: string,
    resourceType: ResourceActivityResourceType,
) => {
    removeBufferedOptimisticResourceComment(resourceType, resourceId, optimisticComment.id);
    emitResourceCommentEvent({
        action: 'deleted',
        commentId: optimisticComment.id,
        resourceId,
        resourceType,
    });
    emitResourceCommentEvent({
        action: 'created',
        comment: persistedComment,
        resourceId,
        resourceType,
    });
};

export const removeOptimisticResourceComment = (
    optimisticComment: ResourceComment,
    resourceId: string,
    resourceType: ResourceActivityResourceType,
) => {
    removeBufferedOptimisticResourceComment(resourceType, resourceId, optimisticComment.id);
    emitResourceCommentEvent({
        action: 'deleted',
        commentId: optimisticComment.id,
        resourceId,
        resourceType,
    });
};

export const subscribeResourceCommentEvents = (handler: (detail: ResourceCommentEventDetail) => void) => {
    const listener = (event: Event) => {
        handler((event as CustomEvent<ResourceCommentEventDetail>).detail);
    };

    window.addEventListener(RESOURCE_COMMENT_EVENT, listener);

    return () => window.removeEventListener(RESOURCE_COMMENT_EVENT, listener);
};

export const emitResourceCommentTargetFocusEvent = (detail: ResourceCommentTargetFocusEventDetail) => {
    pendingResourceCommentTargetFocus = detail;
    clearTimeout(pendingResourceCommentTargetFocusTimer);
    pendingResourceCommentTargetFocusTimer = setTimeout(() => {
        pendingResourceCommentTargetFocus = undefined;
    }, 10_000);
    window.dispatchEvent(
        new CustomEvent<ResourceCommentTargetFocusEventDetail>(RESOURCE_COMMENT_TARGET_FOCUS_EVENT, { detail }),
    );
};

export const subscribeResourceCommentTargetFocusEvents = (
    handler: (detail: ResourceCommentTargetFocusEventDetail) => boolean | void,
) => {
    const handleDetail = (detail: ResourceCommentTargetFocusEventDetail) => {
        if (handler(detail) === true && pendingResourceCommentTargetFocus === detail) {
            pendingResourceCommentTargetFocus = undefined;
            clearTimeout(pendingResourceCommentTargetFocusTimer);
        }
    };
    const listener = (event: Event) => {
        handleDetail((event as CustomEvent<ResourceCommentTargetFocusEventDetail>).detail);
    };

    window.addEventListener(RESOURCE_COMMENT_TARGET_FOCUS_EVENT, listener);
    if (pendingResourceCommentTargetFocus) {
        queueMicrotask(() => {
            if (pendingResourceCommentTargetFocus) {
                handleDetail(pendingResourceCommentTargetFocus);
            }
        });
    }

    return () => window.removeEventListener(RESOURCE_COMMENT_TARGET_FOCUS_EVENT, listener);
};

export const emitResourceActivityOpenEvent = (detail: ResourceActivityOpenEventDetail) => {
    window.dispatchEvent(new CustomEvent<ResourceActivityOpenEventDetail>(RESOURCE_ACTIVITY_OPEN_EVENT, { detail }));
};

export const subscribeResourceActivityOpenEvents = (handler: (detail: ResourceActivityOpenEventDetail) => void) => {
    const listener = (event: Event) => {
        handler((event as CustomEvent<ResourceActivityOpenEventDetail>).detail);
    };

    window.addEventListener(RESOURCE_ACTIVITY_OPEN_EVENT, listener);

    return () => window.removeEventListener(RESOURCE_ACTIVITY_OPEN_EVENT, listener);
};
